/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1902247966 — PaymentCT10Screen */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrencyInSubUnits } from '../../../../shared';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { usePageTracking } from '../../../../shared/hooks/usePageTracking';
import { ContentBox, ErrorText, Input, InputWrapper, ScreenTitle, ScreenWrapper } from '../../../../shared/ui';
import { EVENT_NAMES, useSendEventLog } from '../../../event-log';
import { withOrderHydration } from '../../../order/hooks/withOrderHydration';
import { PaymentOrFeedback } from '../../components/PaymentOrFeedback/PaymentOrFeedback';
import { PriceWithoutDiscount } from '../../components/PriceDisplay/PriceWithoutDiscount/PriceWithoutDiscount';
import { useAmountValidation } from '../../hooks/useAmountValidation';
import { useOrderAmount } from '../../hooks/useOrderAmount';
import { AMOUNT_INPUT_DEBOUNCE_MS, LIMITS } from '../../payment.constants';
import { normalizeDecimalInput } from '../../payment.utils';
import styles from './PaymentCT10Screen.module.css';

const PaymentCT10ScreenBody = () => {
    const { t } = useTranslation();

    const vm = useAppStore((s) => s.vm);

    const [displayAmount, setDisplayAmount] = useState<string>('');
    const [isPaymentButtonVisible, setIsPaymentButtonVisible] = useState<boolean>(false);
    const { isAmountSent, submitAmount } = useOrderAmount({ debounceMs: AMOUNT_INPUT_DEBOUNCE_MS });
    const { sendEvent } = useSendEventLog();
    const [error, setError] = useState<string>();
    const { max: maxLimit, validate, limitToMaxAmount } = useAmountValidation({ min: LIMITS.PAYMENT_CT10.MIN });

    usePageTracking('payment CT10');

    const hasAmountEventFiredRef = useRef(false);
    useEffect(() => {
        if (isAmountSent && !hasAmountEventFiredRef.current) {
            sendEvent({ name: EVENT_NAMES.amountSubmitted.ct10 });
            hasAmountEventFiredRef.current = true;
        }
    }, [isAmountSent]);

    const onInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const result = normalizeDecimalInput(event.target.value);
        if (result === null) return;

        let { display, value: enteredAmount } = result;
        const validationError = validate(enteredAmount);

        if (validationError && enteredAmount > maxLimit) {
            enteredAmount = limitToMaxAmount(enteredAmount);
            display = String(enteredAmount);
            setError(validationError);
            setIsPaymentButtonVisible(false);
        } else {
            setError('');
        }

        setIsPaymentButtonVisible(enteredAmount > 0 && enteredAmount <= maxLimit);
        setDisplayAmount(display);

        // CT10 flow does not honour campaign discounts — submit the entered
        // amount as-is (converted to subunits).
        const amountInSubunits = getCurrencyInSubUnits(enteredAmount);
        submitAmount(amountInSubunits);
    };

    return (
        <ScreenWrapper>
            <ScreenTitle>{t('Enter amount')}</ScreenTitle>
            <ContentBox>
                <InputWrapper>
                    <Input
                        type="text"
                        inputMode="decimal"
                        max={maxLimit}
                        onChange={onInputChangeHandler}
                        value={displayAmount}
                        placeholder={t<string>('Amount in currency', {
                            currency: t(`Currency.${vm?.currency_code}`),
                        })}
                    />
                    <ErrorText>{error}</ErrorText>
                </InputWrapper>
                <div className={styles.payText}>{t('Order payment')}</div>
                
                <PriceWithoutDiscount />

                <PaymentOrFeedback
                    isVisible={isPaymentButtonVisible}
                    isPending={!isAmountSent}
                    screenName="payment-ct10"
                />

            </ContentBox>
        </ScreenWrapper>
    );
};

const PaymentCT10Screen = withOrderHydration(PaymentCT10ScreenBody);

export { PaymentCT10Screen };
